import type { KnowledgeBase } from '@ralph/contracts'
import clsx from 'clsx'
import { CircleDashed, Clock, Globe, Hourglass, Lock, ShieldCheck, TriangleAlert } from 'lucide-react'
import type { CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import { GroupAvatar } from '@/components/groups/GroupAvatar.tsx'
import { riseDelay, StudioRow } from '@/components/ui/admin-chrome.tsx'
import { Badge, Spinner } from '@/components/ui/primitives.tsx'
import { useI18n } from '@/i18n/index.ts'
import { type BaseStanding, baseStanding, censusLine, type WhyLine, whyLine } from './knowledge-base.ts'

/**
 * One knowledge base in the list (PRD-26 *The interface* → Knowledge).
 *
 * Three lines, each answering one question a person scanning the list is asking:
 *
 *  1. which one, and is it all right — the name, and a standing mark that is never colour alone;
 *  2. why do I have it — "Owned by Finance EMEA · only its managers", "Shared with Legal ·
 *     includes you", "Shared with everyone", "Only you", with the group's avatar wherever a group
 *     is the reason (GRP-102);
 *  3. what is in it — how much of it answers, and the folders that feed it, the urgent fact first.
 *
 * Maintainers also see what waits on them: a sharing request in review, and requests to let an
 * agent answer from it for everyone it reaches.
 *
 * The row is a link: the selection lives in the address (`/knowledge/:id`), so a notification, a
 * bookmark or the back button lands on the same base, and it can be opened in a new tab.
 */
export const BaseRow = ({
  base,
  to,
  selected,
  index
}: {
  base: KnowledgeBase
  to: string
  selected: boolean
  index: number
}) => {
  // Inside the component body, never at module scope: a capture up there freezes the locale at
  // import and renders the previous language with nothing failing (PRD-27 F-3).
  const fmt = useI18n()
  const t = fmt.t.knowledge
  const standing = baseStanding(base)
  const why = whyLine(fmt, base)
  return (
    <Link
      to={to}
      aria-current={selected ? 'page' : undefined}
      className="block rounded-card focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
    >
      <StudioRow
        style={riseDelay(index)}
        className={clsx('px-3.5 py-3', selected && 'border-rule-strong bg-card-solid shadow-glass')}
      >
        <p className="flex items-center gap-2">
          <BaseMark standing={standing} />
          <span className="min-w-0 flex-1 truncate text-[13.5px] font-semibold tracking-[-0.014em]">{base.name}</span>
        </p>
        <WhyText why={why} className="mt-1.5" />
        <p className="readout mt-1 truncate text-fg-subtle">{censusLine(fmt, base, fmt.formatAgo)}</p>
        {(base.pendingRequest !== null || base.pendingLendingRequests > 0) && (
          <p className="mt-2 flex flex-wrap gap-1.5">
            {base.pendingRequest !== null && (
              <Badge tone="primary">
                <Hourglass size={9} strokeWidth={2.6} aria-hidden />
                {t.row.waitingForReview}
              </Badge>
            )}
            {base.pendingLendingRequests > 0 && (
              <Badge tone="primary">{t.row.requestsToDecide(fmt, base.pendingLendingRequests)}</Badge>
            )}
          </p>
        )}
      </StudioRow>
    </Link>
  )
}

/** The why-line with its glyph: a group's avatar where a group is the reason, a lock, a globe, a shield. */
export const WhyText = ({ why, className, style }: { why: WhyLine; className?: string; style?: CSSProperties }) => (
  <p className={clsx('flex min-w-0 items-center gap-1.5 text-[12.5px] text-fg-muted', className)} style={style}>
    <WhyGlyph why={why} />
    <span className="min-w-0 truncate">{why.text}</span>
  </p>
)

const WhyGlyph = ({ why }: { why: WhyLine }) => {
  if (why.group) return <GroupAvatar group={why.group} size={16} />
  const Icon = why.mark === 'everyone' ? Globe : why.mark === 'administrator' ? ShieldCheck : Lock
  return (
    <span aria-hidden className="flex h-[16px] w-[16px] shrink-0 items-center justify-center text-fg-subtle">
      <Icon size={12} strokeWidth={2.2} />
    </span>
  )
}

/**
 * The standing mark: a dot for the quiet states, a glyph for the ones a person must act on, and
 * a word for everyone who cannot see either (AGM-124).
 */
export const BaseMark = ({ standing }: { standing: BaseStanding }) => {
  // In the body, never at module scope (PRD-27 F-3, LOC-012).
  const { t } = useI18n()
  const word = <span className="sr-only">{t.knowledge.standing[standing]}. </span>
  switch (standing) {
    case 'ready':
      return (
        <>
          <span className="h-[7px] w-[7px] shrink-0 rounded-full bg-grounded" aria-hidden />
          {word}
        </>
      )
    case 'reading':
      return (
        <>
          <Spinner size={10} className="shrink-0 text-primary" />
          {word}
        </>
      )
    case 'empty':
      return (
        <>
          <span className="h-[7px] w-[7px] shrink-0 rounded-full border border-rule-strong" aria-hidden />
          {word}
        </>
      )
    case 'silent':
      return (
        <>
          <CircleDashed size={11} strokeWidth={2.4} className="shrink-0 text-alert" aria-hidden />
          {word}
        </>
      )
    case 'attention':
      return (
        <>
          <TriangleAlert size={11} strokeWidth={2.4} className="shrink-0 text-alert" aria-hidden />
          {word}
        </>
      )
    case 'stale':
      return (
        <>
          <Clock size={11} strokeWidth={2.4} className="shrink-0 text-alert" aria-hidden />
          {word}
        </>
      )
  }
}
